import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import {
  ChangeEvent,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdDelete, MdOutlineModeEdit, MdRemoveRedEye } from "react-icons/md";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import PopUpForm from "../../components/PopUpForm";
import { localizedTextDefaultValue } from "../../constants/localizedTextDefaultValue";
import { useApi } from "../../services/api";
import { LocalizedText, Project } from "../../services/generated/client";
import {
  createProject,
  getAllProjects,
  removeProject,
  updateProject,
} from "../../services/project/projectServices";
import styles from "./styles.module.css";

const Projects = () => {
  const { withErrorHandling } = useApi();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project>({
    _id: {},
    name: localizedTextDefaultValue,
    mainImageUrl: "",
    header1: localizedTextDefaultValue,
    header2: localizedTextDefaultValue,
    paragraph1: localizedTextDefaultValue,
    paragraph2: localizedTextDefaultValue,
    imageUrls: [],
    completed: false,
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [view, setView] = useState<boolean>(false);
  const [languageEditing, setLanguageEditing] =
    useState<keyof LocalizedText>("ka");

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen]);

  useEffect(() => {
    withErrorHandling(async () => {
      const response = await getAllProjects();
      if (response) {
        setProjects(response);
      }
    });
  }, []);

  const handleEdit = (project: Project) => {
    setSelectedProject(project);
    setIsAdding(false);
    setView(false);
    setIsModalOpen(true);
  };

  const handleView = (project: Project) => {
    setSelectedProject(project);
    setIsAdding(false);
    setView(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (_id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete ${name}`)) {
      await removeProject(_id);
      setProjects((prev) =>
        prev.filter((project) => project._id.toString() !== _id)
      );
    }
  };

  const handleUpdateProject = async (e: FormEvent) => {
    e.preventDefault();

    const {
      _id,
      name,
      mainImageUrl,
      header1,
      header2,
      paragraph1,
      paragraph2,
      imageUrls,
      completed,
    } = selectedProject;

    const cleanedImageUrls = imageUrls.filter((url) => url.trim() !== "");

    const payload = {
      name,
      mainImageUrl,
      header1,
      header2,
      paragraph1,
      paragraph2,
      imageUrls: cleanedImageUrls,
      completed,
    };

    await withErrorHandling(async () => {
      if (isAdding) {
        const newProject = await createProject(payload);
        setProjects((prev) => [...prev, newProject]);
        alert("Project created successfully!");
      } else if (!view) {
        await updateProject(_id.toString(), payload);
        setProjects((prev) =>
          prev.map((project) =>
            project._id === _id ? selectedProject : project
          )
        );
        alert("Project updated successfully!");
      }
      setIsModalOpen(false);
    });
  };

  const handleAddProject = () => {
    setSelectedProject({
      _id: {},
      name: localizedTextDefaultValue,
      mainImageUrl: "",
      header1: localizedTextDefaultValue,
      header2: localizedTextDefaultValue,
      paragraph1: localizedTextDefaultValue,
      paragraph2: localizedTextDefaultValue,
      imageUrls: [],
      completed: false,
    });
    setIsAdding(true);
    setIsModalOpen(true);
  };

  const columns = useMemo<MRT_ColumnDef<Project>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row }) => row.original.name?.[languageEditing] || "",
      },
      {
        accessorKey: "header1",
        header: "Header 1",
        Cell: ({ row }) => row.original.header1?.[languageEditing] || "",
      },
      {
        accessorKey: "header2",
        header: "Header 2",
        Cell: ({ row }) => row.original.header2?.[languageEditing] || "",
      },

      {
        accessorKey: "completed",
        header: "Completed",
        Cell: ({ cell }) => (cell.getValue<boolean>() ? "Yes" : "No"),
      },
      {
        header: "Actions",
        Cell: ({ row }: { row: { original: Project } }) => (
          <div className={styles.actionBtns}>
            <button
              onClick={() => handleView(row.original)}
              className={styles.tableBtn}
            >
              <MdRemoveRedEye />
            </button>
            <button
              onClick={() => handleEdit(row.original)}
              className={styles.tableBtn}
            >
              <MdOutlineModeEdit />
            </button>
            <button
              onClick={() =>
                handleDelete(row.original._id.toString(), row.original.name.en)
              }
              className={`${styles.tableBtn} ${styles.delete}`}
            >
              <MdDelete />
            </button>
          </div>
        ),
      },
    ],
    [languageEditing]
  );

  const handleMultiLanguageInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id as keyof Project;
    const value = e.target.value;

    setSelectedProject((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] as LocalizedText),
        [languageEditing]: value,
      },
    }));
  };

  return (
    <div className={styles.mainContent}>
      <h2>Manage Projects</h2>
      <div className={styles.tableWrapper}>
        <MaterialReactTable
          columns={columns}
          data={projects}
          renderTopToolbarCustomActions={() => (
            <div style={{ display: "flex", gap: "20px" }}>
              <button className={styles.addBtn} onClick={handleAddProject}>
                + Add Project
              </button>
              <div>
                <LanguageSwitcher
                  languageEditing={languageEditing}
                  setLanguageEditing={setLanguageEditing}
                  inForm={false}
                />
              </div>
            </div>
          )}
          muiTableContainerProps={{
            sx: {
              flex: 1,
              overflowY: "hidden",
            },
          }}
          muiTablePaperProps={{
            sx: {
              flex: 1,
              display: "flex",
              flexDirection: "column",
            },
          }}
          muiTableBodyProps={{
            sx: {
              overflowY: "auto",
              height: "100%",
            },
          }}
          muiTableProps={{
            sx: {
              maxHeight: "100%",
              gridTemplateRows: "auto 1fr",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              overflow: "auto",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          layoutMode="grid"
        />
      </div>

      {isModalOpen && selectedProject && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} ref={modalRef}>
            <button
              className={styles.closeButton}
              onClick={() => setIsModalOpen(false)}
            >
              X
            </button>

            <LanguageSwitcher
              languageEditing={languageEditing}
              setLanguageEditing={setLanguageEditing}
              inForm={true}
            />
            <PopUpForm
              title={"Project"}
              submitLabel={isAdding ? "Create" : "Update"}
              onSubmit={handleUpdateProject}
              languageEditing={languageEditing}
              isView={view}
              isAdding={isAdding}
              fields={[
                {
                  id: "mainImageUrl",
                  label: "Main Image",
                  type: "imageUrl",
                  value: selectedProject.mainImageUrl,
                  onImageChange: (_, url) =>
                    setSelectedProject((prev) => ({
                      ...prev,
                      mainImageUrl: url,
                    })),
                },
                {
                  id: "name",
                  label: `Name ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedProject?.name?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "header1",
                  label: `Header 1 ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedProject?.header1?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "header2",
                  label: `Header 2 ${languageEditing.toUpperCase()}`,
                  type: "text",
                  value:
                    selectedProject?.header2?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "paragraph1",
                  label: `Paragraph 1 ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedProject?.paragraph1?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "paragraph2",
                  label: `Paragraph 2 ${languageEditing.toUpperCase()}`,
                  type: "textarea",
                  value:
                    selectedProject?.paragraph2?.[
                      languageEditing as keyof LocalizedText
                    ] || "",
                  onChange: (e) =>
                    handleMultiLanguageInputChange(
                      e as ChangeEvent<HTMLInputElement>
                    ),
                },
                {
                  id: "imageUrls",
                  label: "Gallery Images",
                  type: "imageUrls",
                  values: selectedProject.imageUrls,
                  onImageChange: (index, value) => {
                    const newImages = [...selectedProject.imageUrls];
                    newImages[index] = value;
                    setSelectedProject((prev) => ({
                      ...prev,
                      imageUrls: newImages,
                    }));
                  },
                  onAddImage: () =>
                    setSelectedProject((prev) => ({
                      ...prev,
                      imageUrls: [...prev.imageUrls, ""],
                    })),
                  onRemoveImage: (index) =>
                    setSelectedProject((prev) => ({
                      ...prev,
                      imageUrls: prev.imageUrls.filter((_, i) => i !== index),
                    })),
                },
                {
                  id: "completed",
                  label: "Completed?",
                  type: "checkbox",
                  value: selectedProject?.completed ? "true" : "false",
                  onChange: (e) =>
                    setSelectedProject((prev) => ({
                      ...prev,
                      completed: (e.target as HTMLInputElement).checked,
                    })),
                },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};
export default Projects;
